import {
  Box,
  Button,
  Divider,
  Drawer,
  Flex,
  PasswordInput,
  Select,
  Space,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import bgImg from "assets/images/pngs/bg.png";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RoutesString from "@/pages/routesString";
import CardDetailActionPopup from "./popup/CardDetailActionPopup";
import { useDisclosure } from "@mantine/hooks";
import { useForm, yupResolver } from "@mantine/form";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import OtpModal from "@/components/common/modal/otp-modal/OtpModal";
import {
  useInitLockDebitCardMutation,
  useInitUnlockDebitCardMutation,
  useLockDebitCardMutation,
  useUnlockDebitCardMutation,
} from "@/services/cardAPI";
import { useGenerateOTPMutation } from "@/services/authAPI";
import { notifications } from "@mantine/notifications";
import { RESULT_CODE } from "@/constants/enum";
import { SHA256 } from "crypto-js";
import {
  CardApprovalProcessInfo,
  CardAuthObject,
  GetDebitCardDetailResponse,
} from "@/dto/card/response/CardResponse";
import { getOtpPhoneNumber } from "@/utils/otpPhone";

const AUTH_TYPE_CODE: Record<string, number> = {
  SMS_OTP: 1,
  SMSOTP: 1,
  SMART_OTP: 3,
  SmartOTP: 3,
};

interface Props {
  opened: boolean;
  close: () => void;
  label: string;
  cardId?: number;
  cardDetail?: GetDebitCardDetailResponse;
}

const AUTH_LABEL_MAP: Record<string, string> = {
  SMART_OTP: "Smart OTP",
  SMS_OTP: "SMS OTP",
  SmartOTP: "Smart OTP",
  SMSOTP: "SMS OTP",
};

const formatVnd = (n?: number, ccy?: string) =>
  `${Number(n ?? 0).toLocaleString("vi-VN")} ${ccy || "VND"}`;

const PASSWORD_RULES = [
  "LENGTH_FROM_6_CHARACTERS",
  "NOT_CONSECUTIVE_NUMBER",
  "NOT_SAME_NUMBER",
  "NOT_SAME_NEAREST",
] as const;
type PasswordRule = (typeof PASSWORD_RULES)[number];

const CardDetailActionDrawer = ({
  opened,
  close,
  label,
  cardId,
  cardDetail,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenedOTPModal, { open: openOTPModal, close: closeOTPModal }] =
    useDisclosure(false);
  const [openedPopup, { open: openPopup, close: closePopup }] =
    useDisclosure(false);
  const [initLockDebitCard, { isLoading: isInitLockLoading }] =
    useInitLockDebitCardMutation();
  const [initUnlockDebitCard, { isLoading: isInitUnlockLoading }] =
    useInitUnlockDebitCardMutation();
  const [generateOTP, { isLoading: isGeneratingOtp }] =
    useGenerateOTPMutation();
  const [lockDebitCard, { isLoading: isLockingCard }] =
    useLockDebitCardMutation();
  const [unlockDebitCard, { isLoading: isUnlockingCard }] =
    useUnlockDebitCardMutation();
  const [lockRefId, setLockRefId] = useState<string>("");
  const [lockApprovalInfo, setLockApprovalInfo] =
    useState<CardApprovalProcessInfo | null>(null);
  const [lockAuthObjects, setLockAuthObjects] = useState<CardAuthObject[]>([]);
  const [authMethod, setAuthMethod] = useState<string>("");
  const [otpId, setOtpId] = useState<string>("");
  const passwordSchema = Yup.object().shape({
    currentPassword: Yup.string().required(`${t("REQUIRE")}`),
    newPassword: Yup.string()
      .required(`${t("REQUIRE")}`)
      .matches(/^[^\s]{6,20}$/, `${t("LENGTH_FROM_6_CHARACTERS")}`)
      .required(`${t("REQUIRE")}`),

    confirmPassword: Yup.string().required(`${t("REQUIRE")}`),
  });
  const form = useForm({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      otp: "",
    },
    validate: yupResolver(passwordSchema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const { newPassword } = form.values;

  const passwordChecks: Record<PasswordRule, boolean> = {
    LENGTH_FROM_6_CHARACTERS: newPassword.length >= 6,
    NOT_CONSECUTIVE_NUMBER:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword),
    NOT_SAME_NUMBER: !/\s/.test(newPassword) && newPassword.length > 0,
    NOT_SAME_NEAREST:
      newPassword === form.values.confirmPassword && newPassword.length > 0,
  };

  const actions = [
    {
      label: t("LOCK_CARD"),
      type: "LOCK",
    },
    {
      label: t("UNLOCK_CARD"),
      type: "UNLOCK",
    },
    {
      label: t("CONTINUE_PAYMENT"),
      type: "PAYMENT",
    },
    {
      label: t("CHANGE_PIN"),
      type: "PIN",
    },
  ];

  const titleModal =
    actions.find((action) => action.type === label)?.label || "";

  const renderPasswordRequirements = () => (
    <Flex direction="column" gap={8}>
      {PASSWORD_RULES.map((rule) => (
        <Flex key={rule} gap="xs" align="center">
          <ThemeIcon
            size="xs"
            color={passwordChecks[rule] ? "green" : "#D8DDE2"}
            radius="xl"
          >
            <IconCheck size={8} color="white" />
          </ThemeIcon>
          <Text fz="xs">{t(rule)}</Text>
        </Flex>
      ))}
    </Flex>
  );

  const authentication = () => (
    <Box sx={{ display: "flex", gap: "16px", flexDirection: "column" }}>
      <Box sx={{ display: "flex", gap: "8px", flexDirection: "column" }}>
        <Text sx={{ color: "#131618", fontWeight: 600, fontSize: "16px" }}>
          {t("AUTHENTICATION_MODE")}
        </Text>
        <Select
          data={[
            { value: "SmartOTP", label: "Smart OTP" },
            { value: "SMSOTP", label: "SMS OTP" },
          ]}
          radius="md"
          defaultValue="SMSOTP"
          rightSection={
            <IconChevronDown size={18} stroke={1.8} color="#E1E4EA" />
          }
          sx={{
            ".mantine-Select-input": {
              backgroundColor: "#FDFDFD",
              borderColor: "#E1E4EA",
              color: "#131618",
              "&:hover": {
                borderColor: "#1E6FA1",
              },
              "&:focus, &:focus-within": {
                borderColor: "#2A81D0 !important",
                boxShadow: "none",
              },
            },
            ".mantine-Select-item": {
              "&[data-hovered]": {
                backgroundColor: "#D7E8FA",
                color: "#1E6FA1",
              },
              "&[data-selected]": {
                backgroundColor: "#2A81D0",
                color: "#FFFFFF",
              },
            },
            "&[data-opened] .mantine-Select-input": {
              borderColor: "#2A81D0",
            },
          }}
        />
      </Box>
    </Box>
  );

  const isLockToggleAction = label === "LOCK" || label === "UNLOCK";
  const errorTitle =
    label === "UNLOCK" ? t("INITIATE_CARD_UNLOCK") : t("INITIATE_CARD_LOCK");

  const handleCloseOtp = () => {
    closeOTPModal();
  };

  const handleConfirmOTP = async () => {
    if (!isLockToggleAction) {
      closeOTPModal();
      openPopup();
      return;
    }
    if (!cardId || !lockRefId) {
      notifications.show({
        title: t("ERROR"),
        message: t("MISSING_REQUEST_INFO"),
        color: "red",
      });
      return;
    }
    try {
      const authTypeCode = AUTH_TYPE_CODE[authMethod] ?? 1;
      const payload = {
        refId: lockRefId,
        cardId,
        authObject: {
          authType: authTypeCode,
          otpId,
          otp: SHA256(form.values.otp || "").toString(),
        },
      };
      const res =
        label === "UNLOCK"
          ? await unlockDebitCard(payload).unwrap()
          : await lockDebitCard(payload).unwrap();
      if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
        notifications.show({
          title: t("ERROR"),
          message:
            res.body?.resultDesc ||
            (label === "UNLOCK" ? t("UNLOCK_CARD_FAILED") : t("LOCK_CARD_FAILED")),
          color: "red",
        });
        return;
      }
      closeOTPModal();
      close();
      navigate(RoutesString.CardActionResult, {
        state: {
          actionType: label === "UNLOCK" ? "UNLOCK" : "LOCK",
          detailObjectList: res.body?.detailObjectList || [],
        },
      });
    } catch (error) {
      notifications.show({
        title: t("ERROR"),
        message: t("CONNECTION_ERROR"),
        color: "red",
      });
    }
  };

  const handleSubmit = async () => {
    if (isLockToggleAction) {
      if (!lockRefId) {
        notifications.show({
          title: t("ERROR"),
          message: t("TPL_REQUEST_NOT_INITIATED", { p0: label === "UNLOCK" ? t("UNLOCK_CARD_LOWER_ALT") : t("LOCK_CARD_LOWER_ALT") }),
          color: "red",
        });
        return;
      }
      try {
        const otpResponse = await generateOTP({
          objectId: "1234",
          objectType: "tranfer",
          otpType: "SMS_OTP",
          phoneNumber: "0987978978",
        }).unwrap();
        if (otpResponse.body?.resultCode !== RESULT_CODE.SUCCESS) {
          notifications.show({
            title: t("ERROR"),
            message: otpResponse.body?.resultDesc || t("GET_OTP_FAILED"),
            color: "red",
          });
          return;
        }
        setOtpId(otpResponse.body?.otpId || "");
        close();
        openOTPModal();
      } catch (error) {
        notifications.show({
          title: t("ERROR"),
          message: t("CONNECTION_ERROR"),
          color: "red",
        });
      }
      return;
    }
    close();
    openOTPModal();
  };

  /** Gửi lại OTP trong modal — gọi lại generateOTP y hệt lúc submit, throw khi fail để modal giữ trạng thái hết hạn cho bấm lại. */
  const handleResendOtp = async () => {
    const otpResponse = await generateOTP({
      objectId: "1234",
      objectType: "tranfer",
      otpType: "SMS_OTP",
      phoneNumber: getOtpPhoneNumber(),
    }).unwrap();
    if (otpResponse.body?.resultCode !== RESULT_CODE.SUCCESS) {
      notifications.show({
        title: t("ERROR"),
        message: otpResponse.body?.resultDesc || t("RESEND_OTP_FAILED"),
        color: "red",
      });
      throw new Error("resend otp failed");
    }
    setOtpId(otpResponse.body?.otpId || "");
    notifications.show({
      title: t("NOTICE"),
      message: t("OTP_RESENT"),
      color: "green",
    });
  };

  useEffect(() => {
    if (opened) form.reset();
  }, [opened]);

  useEffect(() => {
    if (!opened || !isLockToggleAction || !cardId) return;
    setLockRefId("");
    setLockApprovalInfo(null);
    setLockAuthObjects([]);
    setAuthMethod("");
    setOtpId("");
    (async () => {
      try {
        const res =
          label === "UNLOCK"
            ? await initUnlockDebitCard({ cardId }).unwrap()
            : await initLockDebitCard({ cardId }).unwrap();
        if (res.body?.resultCode !== RESULT_CODE.SUCCESS) {
          notifications.show({
            title: t("ERROR"),
            message: res.body?.resultDesc || t("TPL_ACTION_FAILED", { p0: errorTitle }),
            color: "red",
          });
          return;
        }
        setLockRefId(res.body.refId || "");
        setLockApprovalInfo(res.body.approvalProcessInfo || null);
        const authObjs = res.body.authObjects || [];
        setLockAuthObjects(authObjs);
        if (authObjs[0]?.authType) setAuthMethod(authObjs[0].authType);
      } catch (error) {
        notifications.show({
          title: t("ERROR"),
          message: t("CONNECTION_ERROR"),
          color: "red",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, label, cardId]);

  return (
    <>
      <Drawer
        opened={opened}
        onClose={close}
        withCloseButton
        position="right"
        size={548}
        styles={{ body: { padding: 0 } }}
        title={
          <Text fw={500} fz={20}>
            {titleModal}
          </Text>
        }
      >
        <Divider />
        {(label === "LOCK" || label === "UNLOCK" || label === "PAYMENT") && (
          <Box p={20}>
            <Text fw={700} fz={16}>
              {t("CARD_INFORMANTION")}
            </Text>
            <Space h={12} />
            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: "8px",
                padding: "20px 16px",
                borderRadius: "8px",
                backgroundImage: `url(${bgImg})`,
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                color: "white",
              }}
            >
              <Text fw={500} fz={16}>
                {cardDetail?.cardNumberMasked || "-"}
              </Text>
              <Text fz={12}>{cardDetail?.accountNo || "-"}</Text>
              <Text fz={12}>
                {formatVnd(cardDetail?.availableBalance, cardDetail?.currency)}
              </Text>
            </Box>
            <Space h={18} />
            <Text fz={16} fw={700}>
              {t("APPROVAL_INFORMATION")}
            </Text>
            <Space h={12} />
            <Stack
              style={{
                border: "1px solid #18181B2E",
                borderRadius: 16,
                padding: 12,
                gap: 12,
              }}
            >
              <Flex justify="space-between">
                <Text>{t("APPROVAL_ID")}</Text>
                <Text fw={600} fz={16}>
                  {lockApprovalInfo?.approvalProcessId || "-"}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text>{t("APPROVAL_LEVEL")}</Text>
                <Text fw={600} fz={16}>
                  {lockApprovalInfo?.totalApprovalLevel ??
                    lockApprovalInfo?.numberOfApprovals ??
                    "-"}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text>{t("REVIEWER")}</Text>
                <Text fw={600} fz={16} c="blue">
                  {/* {lockApprovalInfo?.approvalUserNames?.join(", ") ||
                    lockApprovalInfo?.nextLevelApprovers
                      ?.map((u) => u.approvalUserFullName)
                      .filter(Boolean)
                      .join(", ") ||
                    t("SELECT")} */}
                </Text>
              </Flex>
            </Stack>
            <Space h={20} />

            <Box sx={{ display: "flex", gap: "16px", flexDirection: "column" }}>
              <Box
                sx={{ display: "flex", gap: "8px", flexDirection: "column" }}
              >
                <Text
                  sx={{
                    color: "#131618",
                    fontWeight: 600,
                    fontSize: "16px",
                  }}
                >
                  {t("AUTHENTICATION_MODE")}
                </Text>
                <Select
                  data={(lockAuthObjects.length
                    ? lockAuthObjects
                    : [
                        { authType: "SMART_OTP", authDes: "Smart OTP" },
                        { authType: "SMS_OTP", authDes: "SMS OTP" },
                      ]
                  ).map((a) => ({
                    value: a.authType,
                    label:
                      a.authDes || AUTH_LABEL_MAP[a.authType] || a.authType,
                  }))}
                  radius="md"
                  value={authMethod}
                  onChange={(v) => setAuthMethod(v || "")}
                  rightSection={
                    <IconChevronDown size={18} stroke={1.8} color="#E1E4EA" />
                  }
                />
              </Box>
            </Box>
          </Box>
        )}

        {label === "PIN" && (
          <Box p={20}>
            <Text fw={700} fz={16}>
              {t("CHANGE_CARD_PIN")}
            </Text>
            <Space h={12} />
            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: "8px",
                padding: "20px 16px",
                borderRadius: "8px",
                backgroundImage: `url(${bgImg})`,
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                color: "white",
              }}
            >
              <Text fw={500} fz={16}>
                {cardDetail?.cardNumberMasked || "-"}
              </Text>
              <Text fz={12}>{cardDetail?.accountNo || "-"}</Text>
            </Box>
            <Space h={18} />
            <Box maw={600} w="100%">
              <form
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <PasswordInput
                  label={t("ENTER_OLD_PIN")}
                  radius="md"
                  color="#2A81D0"
                  styles={{
                    label: {
                      color: "#5E656B",
                      marginBottom: "8px",
                    },
                  }}
                  autoComplete="off"
                  {...form.getInputProps("currentPassword")}
                />

                <PasswordInput
                  label={t("NEW_PIN")}
                  radius="md"
                  color="#2A81D0"
                  styles={{
                    label: {
                      color: "#5E656B",
                      marginBottom: "8px",
                    },
                  }}
                  autoComplete="off"
                  {...form.getInputProps("newPassword")}
                />

                <PasswordInput
                  label={t("RE-ENTER_NEW_PIN")}
                  radius="md"
                  color="#2A81D0"
                  styles={{
                    label: {
                      color: "#5E656B",
                      marginBottom: "8px",
                    },
                  }}
                  autoComplete="off"
                  {...form.getInputProps("confirmPassword")}
                />

                {renderPasswordRequirements()}

                <Divider color="#D8DDE2" variant="dashed" />
              </form>
              {authentication()}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "16px",
            backgroundColor: "var(--mantine-color-white)",
            borderTop: "1px solid var(--mantine-color-gray-3)",
            zIndex: 100,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            style={{ borderRadius: "8px" }}
            loading={
              isInitLockLoading || isInitUnlockLoading || isGeneratingOtp
            }
            onClick={handleSubmit}
          >
            {t("CONFIRM")}
          </Button>
        </Box>
      </Drawer>
      <OtpModal
        opened={isOpenedOTPModal}
        onClose={handleCloseOtp}
        onConfirm={handleConfirmOTP}
        expireTime={60}
        otp={form.values.otp}
        onChangeOtp={(value) => form.setFieldValue("otp", value)}
        isLoading={isLockingCard || isUnlockingCard}
        handleSendOtp={handleResendOtp}
        authType={AUTH_TYPE_CODE[authMethod] ?? 1}
      />
      <CardDetailActionPopup
        opened={openedPopup}
        close={closePopup}
        label={label}
      />
    </>
  );
};

export default CardDetailActionDrawer;
